import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  ProfessionalBackOnlinePayload,
  WatchUserWaitingPayload,
} from '@klaro7/shared-types';

import { useSharedSocket } from '@/features/calls/context/CallContext';
import { messages } from '../messages';
import { watchKeys } from './useAvailabilityWatch';

interface OnlineAnnouncementPayload {
  professionalId: string;
  name: string;
  title?: string;
  avatar?: string;
}

interface ListenerOptions {
  onUserWaiting?: (count: number, userName: string) => void;
}

export function useAvailabilityWatchListener(opts: ListenerOptions = {}): void {
  const socket = useSharedSocket();
  const qc = useQueryClient();

  useEffect(() => {
    if (!socket) return;

    const handleBackOnline = (p: ProfessionalBackOnlinePayload): void => {
      qc.invalidateQueries({ queryKey: watchKeys.mine });
      qc.invalidateQueries({ queryKey: watchKeys.forProfessional(p.professionalId) });
      toast.success(messages.toastProfessionalOnline(p.name), {
        duration: 10000,
        action: {
          label: messages.callNow,
          onClick: () => {
            window.location.href = `/consultant/${p.professionalId}`;
          },
        },
      });
    };

    const handleUserWaiting = (p: WatchUserWaitingPayload): void => {
      opts.onUserWaiting?.(p.count, p.userName);
      toast.info(messages.toastUserWaiting(p.userName));
    };

    const handleOnlineAnnouncement = (p: OnlineAnnouncementPayload): void => {
      qc.invalidateQueries({ queryKey: watchKeys.forProfessional(p.professionalId) });
      toast(messages.toastProfessionalConnected(p.name), {
        duration: 10000,
        action: {
          label: messages.callNow,
          onClick: () => {
            window.location.href = `/consultant/${p.professionalId}`;
          },
        },
      });
    };

    socket.on('professional:back-online', handleBackOnline);
    socket.on('watch:user-waiting', handleUserWaiting);
    socket.on('professional:online-announcement', handleOnlineAnnouncement);

    return () => {
      socket.off('professional:back-online', handleBackOnline);
      socket.off('watch:user-waiting', handleUserWaiting);
      socket.off('professional:online-announcement', handleOnlineAnnouncement);
    };
  }, [socket, qc, opts]);
}
